import React, { ReactNode } from 'react'

import Data, { treeNode } from 'data'
import {
  ItemTooltip,
  WithGemTemplateTooltip,
  WithItemTemplateTooltip,
  WithItemTooltip,
  WithPassiveTooltip,
  WithTattooTooltip
} from 'tooltip'

import { ItemDefinition } from '@/data/types'
import { Item, ItemTooltipOptions } from '@/types/item'
import classNames from 'classnames'
import { getIconPath } from 'common'
import { SmallIcon } from 'components/Icon'
import { itemBaseIcon, itemGetBaseColor } from 'logic/item'

export type NodeContent = {
  text?: ReactNode
  html?: string
}

export function WrapperNode({
  html,
  children,
  ...props
}: {
  html?: string
  children: ReactNode
} & React.HTMLAttributes<HTMLSpanElement>) {
  if (html) {
    return <span {...props} dangerouslySetInnerHTML={{ __html: html }} />
  }
  return <span {...props}>{children}</span>
}

export function ItemNode({
  id,
  text,
  html,
  icon,
  variant
}: {
  id: string
  icon?: boolean
  variant?: number | string
} & NodeContent) {
  const item = Data.itemByHash[id] ? Data.itemByHash[id] : Data.uniques[id]
  const iconPath = itemBaseIcon(item)
  if (!item || (icon && !iconPath)) return <WrapperNode html={html}>{text}</WrapperNode>
  const color = Data.uniques[id] ? 'unique' : itemGetBaseColor(item as ItemDefinition) || 'normal'
  const itemId = Data.uniques[id] ? id : item.id
  if (icon) {
    return (
      <WithItemTemplateTooltip name={itemId}>
        <span className="poe-gametip">
          <img className="poe-large-icon" src={getIconPath(iconPath!)} alt={item.name} />
        </span>
      </WithItemTemplateTooltip>
    )
  }
  return (
    <WithItemTemplateTooltip name={itemId} variant={variant ? Number(variant) : undefined}>
      <WrapperNode className={classNames('poe-gametip', { 'poe-icon-only': icon })} html={html}>
        {!!iconPath && <SmallIcon icon={iconPath} />}
        {!icon && <span className={`poe-item-${color}`}>{text}</span>}
      </WrapperNode>
    </WithItemTemplateTooltip>
  )
}

export function PassiveNode({
  id,
  atlas,
  text,
  html
}: {
  id: number | string
  atlas?: boolean
} & NodeContent) {
  const node = treeNode(id, undefined, atlas)
  if (!node) return <WrapperNode html={html}>{text}</WrapperNode>
  return (
    <WithPassiveTooltip id={id} atlas={atlas}>
      <WrapperNode className="poe-gametip" html={html}>
        {!!node.icon && <SmallIcon icon={node.icon} />}
        <span className={`poe-item-passive`}>{text}</span>
      </WrapperNode>
    </WithPassiveTooltip>
  )
}

export function TattooNode({
  id,
  text,
  html
}: {
  id: string
} & NodeContent) {
  const data = Data.tattoos[id]
  if (!data) return <WrapperNode html={html}>{text}</WrapperNode>
  return (
    <WithTattooTooltip id={id}>
      <WrapperNode className="poe-gametip" html={html}>
        {!!data.icon && <SmallIcon icon={data.icon} />}
        <span className={`poe-item-passive`}>{text}</span>
      </WrapperNode>
    </WithTattooTooltip>
  )
}

export function SkillNode({
  id,
  text,
  html
}: {
  id: string
} & NodeContent) {
  const gem = Data.gems[id]
  if (!gem?.active_skill) return <WrapperNode html={html}>{text}</WrapperNode>
  return (
    <WithGemTemplateTooltip id={id}>
      <WrapperNode className="poe-gametip" html={html}>
        {!!gem.active_skill.icon && <SmallIcon icon={gem.active_skill.icon} />}
        <span className={`poe-item-passive`}>{text}</span>
      </WrapperNode>
    </WithGemTemplateTooltip>
  )
}

export function ProfileItemNode({
  item,
  text,
  html,
  icon,
  options
}: {
  item: Item
  icon?: boolean
  options?: ItemTooltipOptions
} & NodeContent) {
  const base = item && ((item.unique && Data.uniques[item.unique]) || Data.items[item.base])
  const iconPath = itemBaseIcon(base, item.map_tier)
  if (!base || (icon && !iconPath)) return <WrapperNode html={html}>{text}</WrapperNode>
  const color = item.unique ? 'unique' : itemGetBaseColor(base as ItemDefinition) || item.rarity
  if (icon) {
    return (
      <WithItemTooltip item={item} options={options}>
        <span className="poe-gametip">
          <img className="poe-large-icon" src={getIconPath(iconPath!)} alt={base.name} />
        </span>
      </WithItemTooltip>
    )
  }
  return (
    <WithItemTooltip item={item} options={options}>
      <WrapperNode className={classNames('poe-gametip', { 'poe-icon-only': icon })} html={html}>
        {!!iconPath && <SmallIcon icon={iconPath} />}
        {!icon && <span className={`poe-item-${color}`}>{text}</span>}
      </WrapperNode>
    </WithItemTooltip>
  )
}

export function TooltipItemNode({ item, options }: { item: Item; options?: ItemTooltipOptions }) {
  return <ItemTooltip item={item} options={options} />
}
